import React, { useCallback, useEffect, useState } from 'react';
import lodash from 'lodash';
import {
   Input,
   InputGroup,
   InputGroupAddon,
   InputGroupText,
} from 'reactstrap';
import PropTypes from 'prop-types';
import withInputErrorMessage from '../withInputErrorMessage';
import withNumberInput from './withNumberInput';

// Adds decimal place to intString
// CASE A:
//    INPUT: 1
//    OUTPUT: 0.01

// CASE B:
//    INPUT: 12
//    OUTPUT: 0.12

// CASE C:
//    INPUT: 123
//    OUTPUT: 1.23

// CASE D:
//    INPUT: 12345678
//    OUTPUT: 123456.78

const absIntStrToAbsFloatStr = (intStr = '') => {
   let absFloatStr = '0.00';

   try {
      let formattedStr = parseInt(intStr).toString();

      while (formattedStr.length < 3) {
         formattedStr = `0${ formattedStr }`;
      }

      absFloatStr = `${ formattedStr.substring(0, formattedStr.length - 2) }.${ formattedStr.substring(formattedStr.length - 2) }`;
   } catch (e) {
      console.log(`Error occurred while transforming absIntStr to absFloatStr`, e);
   }

   return absFloatStr;
};

// Adds the commas in the whole number part of the absFloatStr
const absFloatStrToAbsCurrencyStr = (absFloatStr) => {

   let currencyDisplay = '';

   if (absFloatStr) {
      try {
         const [wholeNum, decimal] = absFloatStr.split('.');

         if (wholeNum.length < 3) {
            currencyDisplay = `${ wholeNum }.${ decimal }`;
         } else {
            let formattedWholeNum = `${ wholeNum.slice(wholeNum.length - 3) }`;
            for (let end = wholeNum.length - 3; end > 0; end -= 3) {
               const start = Math.max(end - 3, 0);
               const valToAppend = wholeNum.slice(start, end);

               formattedWholeNum = `${ valToAppend },${ formattedWholeNum }`;
            }
            currencyDisplay = `${ formattedWholeNum }.${ decimal }`;
         }
      } catch (e) {
         console.log(`Error occurred while transforming absFloatStr to absCurrencyStr with absFloatStr=${ absFloatStr }`, e);
      }
   }

   return currencyDisplay;
};

const CurrencyInput = (props) => {
   const [innerValue, setInnerValue] = useState('');

   useEffect(() => {
      if (props.value === '-1') {
         setInnerValue('-1');
         return;
      }
      if (props.value === 0) {
         setInnerValue('0.00');
         return;
      }

      if (
         props.value !== undefined
         && props.value !== ''
         && !(Number.isNaN(Number(props.value)))
      ) {
         const isNegative = Number(props.value) < 0;
         const absIntStr = Math.abs(Number.parseFloat(props.value))
            .toFixed(2)
            .toString()
            .replace('.', '');

         const absFloatStr = absIntStrToAbsFloatStr(absIntStr);
         if (isNegative) {
            props.onChangeNonEvent(`-${ absFloatStr }`);
         } else {
            props.onChangeNonEvent(absFloatStr);
         }
         const absCurrencyStr = absFloatStrToAbsCurrencyStr(absFloatStr);

         if (isNegative) {
            setInnerValue(`-${ absCurrencyStr }`);
         } else {
            setInnerValue(absCurrencyStr);
         }
      }
   }, [props.value]);

   // Stops 'e' from breaking the input by ignoring the e input
   const handleKeyDown = useCallback((event) => {
      if (event?.key === 'e') {
         event.preventDefault();
      } else if (!props.isNegativeAllowed && event?.key === '-') {
         event.preventDefault();
      }
   }, []);

   // TODO: when 0 is entered, the new input is being ignored
   // TODO: when trying to clear input value, the value is always 0.00
   const handleChange = useCallback((event) => {
      if (props.value === '-1') {
         setInnerValue('-1');
         return;
      }

      const newInputValue = event?.target?.value;
      const userHasDeletedAllInput = lodash.isString(newInputValue) && newInputValue.length === 0;
      if (newInputValue === '0.0' || userHasDeletedAllInput) {
         props.onChangeNonEvent('');
         setInnerValue('');
      } else {
         const formattedStrInputValue = `${ newInputValue }`.replaceAll(',', '').replace('.', '');
         if (newInputValue !== undefined && formattedStrInputValue.length < 14) {
            const intValue = Number.parseInt(formattedStrInputValue);

            if (!(Number.isNaN(Number(intValue)))) {
               const isNegative = intValue < 0;
               const absIntStr = `${ Math.abs(intValue) }`;
               const absFloatStr = absIntStrToAbsFloatStr(absIntStr);
               if (isNegative) {
                  props.onChangeNonEvent(`-${ absFloatStr }`);
               } else {
                  props.onChangeNonEvent(absFloatStr);
               }
               const absCurrencyStr = absFloatStrToAbsCurrencyStr(absFloatStr);
               if (isNegative) {
                  setInnerValue(`-${ absCurrencyStr }`);
               } else {
                  setInnerValue(absCurrencyStr);
               }
            }
         }
      }
   }, []);

   return (
      <InputGroup>
         <InputGroupAddon addonType='prepend'>
            <InputGroupText>$</InputGroupText>
         </InputGroupAddon>
         <Input
            onKeyDown={ handleKeyDown }
            type='text'
            name={ props.name }
            onChange={ handleChange }
            onBlur={ props.onBlur }
            value={ props.isDisabled && props.isValueHiddenWhenDisabled ? '' : innerValue }
            disabled={ props.isDisabled }
            className={ props.className }
         />
      </InputGroup>

   );
};

CurrencyInput.propTypes = {
   name: PropTypes.string.isRequired,
   value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   onBlur: PropTypes.func.isRequired,
   isDisabled: PropTypes.bool,
   isValueHiddenWhenDisabled: PropTypes.bool,
   isNegativeAllowed: PropTypes.bool,
   onChangeNonEvent: PropTypes.func.isRequired,
};

CurrencyInput.defaultProps = {
   value: '',
   isDisabled: false,
   isValueHiddenWhenDisabled: false,
   isNegativeAllowed: false,
};

export default withNumberInput(withInputErrorMessage(CurrencyInput));
